import gsap from 'gsap';

export default class InfiniteGrid {
  constructor({ el, sources, data, originalSize }) {
    this.$container   = el;
    this.sources      = sources;
    this.data         = data;
    this.originalSize = originalSize;

    this.scroll = {
      ease:   0.06,
      current:{ x: 0, y: 0 },
      target: { x: 0, y: 0 },
      last:   { x: 0, y: 0 },
      delta: { x: { c: 0, t: 0 }, y: { c: 0, t: 0 } }
    };

    this.isDragging = false;
    this.drag = { startX: 0, startY: 0, scrollX: 0, scrollY: 0 };

    this.mouse = {
      x: { t: 0.5, c: 0.5 },
      y: { t: 0.5, c: 0.5 },
      press: { t: 0, c: 0 },
    };

    this.items = [];
    this.pendingImages = 0;
    this.imagesLoaded = false;
    this.loadedImages = new Set(); // Track which images have been loaded

    this.onResize     = this.onResize.bind(this);
    this.onWheel      = this.onWheel.bind(this);
    this.onMouseMove  = this.onMouseMove.bind(this);
    this.onMouseDown  = this.onMouseDown.bind(this);
    this.onMouseUp    = this.onMouseUp.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove  = this.onTouchMove.bind(this);
    this.onTouchEnd   = this.onTouchEnd.bind(this);
    this.render       = this.render.bind(this);

    window.addEventListener('resize', this.onResize);
    window.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('mousemove', this.onMouseMove);
    this.$container.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    // 添加觸控事件支持
    this.$container.addEventListener('touchstart', this.onTouchStart, { passive: false });
    window.addEventListener('touchmove', this.onTouchMove, { passive: false });
    window.addEventListener('touchend', this.onTouchEnd);

    this.onResize();
    this.render();
  }

  onResize() {
    this.winW = window.innerWidth;
    this.winH = window.innerHeight;

    // Responsive zoom factor - larger on mobile for better readability
    const isMobile = window.innerWidth < 640;
    const isTablet = window.innerWidth >= 640 && window.innerWidth < 1024;
    const zoomFactor = isMobile ? 10.0 : (isTablet ? 6.0 : 4.0);

    this.tileSize = {
      w: this.winW * zoomFactor,
      h: (this.winW * zoomFactor) * (this.originalSize.h / this.originalSize.w),
    };

    this.scroll.current = { x: 0, y: 0 };
    this.scroll.target  = { x: 0, y: 0 };
    this.scroll.last    = { x: 0, y: 0 };

    this.$container.innerHTML = '';

    const baseItems = this.data.map((d, i) => {
      const scaleX = this.tileSize.w / this.originalSize.w;
      const scaleY = this.tileSize.h / this.originalSize.h;
      const source = this.sources[i % this.sources.length];
      return {
        src: source.src,
        thumbnail: source.thumbnail,
        x:   d.x * scaleX,
        y:   d.y * scaleY,
        w:   d.w * scaleX,
        h:   d.h * scaleY
      };
    });

    this.items = [];
    const repsX = [0, this.tileSize.w];
    const repsY = [0, this.tileSize.h];

    baseItems.forEach(base => {
      repsX.forEach(offsetX => {
        repsY.forEach(offsetY => {
          const el = document.createElement('div');
          el.classList.add('item');
          el.style.width = `${base.w}px`;

          const wrapper = document.createElement('div');
          wrapper.classList.add('item-wrapper');
          el.appendChild(wrapper);

          const itemImage = document.createElement('div');
          itemImage.classList.add('item-image');
          wrapper.appendChild(itemImage);

          const img = new Image();
          // Don't set src yet - we'll lazy load it
          img.dataset.src = base.src.startsWith('http://') || base.src.startsWith('https://')
            ? base.src
            : `/lookbook/${base.src}`;

          // Store thumbnail URL if available for LQIP (Low Quality Image Placeholder)
          if (base.thumbnail) {
            img.dataset.thumbnail = base.thumbnail;
          }

          // Don't use native lazy loading - we have custom logic
          // img.loading = 'lazy';

          // Set dimensions after image loads to maintain aspect ratio
          img.onload = () => {
            const aspectRatio = img.naturalHeight / img.naturalWidth;
            const displayWidth = base.w;
            const displayHeight = displayWidth * aspectRatio;

            itemImage.style.width = `${displayWidth}px`;
            itemImage.style.height = `${displayHeight}px`;

            // Update item dimensions in this.items
            const item = this.items.find(it => it.img === img);
            if (item) {
              item.h = displayHeight;
              item.rect = item.el.getBoundingClientRect();
              item.isLoaded = true;
            }

            // Only add loaded class if this is the full image (not thumbnail)
            // Thumbnail will have blur filter, full image won't
            if (!img.style.filter || img.style.filter === 'blur(0px)') {
              requestAnimationFrame(() => {
                img.classList.add('loaded');
              });
            }
          };

          img.onerror = () => {
            console.error('Failed to load image:', img.dataset.src);
          };

          itemImage.appendChild(img);

          this.$container.appendChild(el);

          this.items.push({
            el,
            container: itemImage,
            wrapper,
            img,
            x: base.x + offsetX,
            y: base.y + offsetY,
            w: base.w,
            h: base.h,
            extraX: 0,
            extraY: 0,
            rect: el.getBoundingClientRect(),
            ease: Math.random() * 0.5 + 0.5,
            isViewed: false,
            viewProgress: 0,
            isLoaded: false,
            imageRequested: false, // Track if we've started loading this image
            fullImageLoaded: false, // Track if full quality image is loaded
            isCleared: false, // Track if blur has been removed
          });
        });
      });
    });

    this.tileSize.w *= 2;
    this.tileSize.h *= 2;

    // Center the grid in the middle of the screen
    // Calculate center position based on tileSize
    const centerX = -(this.tileSize.w / 2 - this.winW / 2);
    const centerY = -(this.tileSize.h / 2 - this.winH / 2);

    this.scroll.current.x = this.scroll.target.x = this.scroll.last.x = centerX;
    this.scroll.current.y = this.scroll.target.y = this.scroll.last.y = centerY;
  }

  onWheel(e) {
    e.preventDefault();
    const factor = 0.4;
    this.scroll.target.x -= e.deltaX * factor;
    this.scroll.target.y -= e.deltaY * factor;
  }

  onMouseDown(e) {
    e.preventDefault();
    this.isDragging = true;
    document.documentElement.classList.add('dragging');
    this.mouse.press.t = 1;
    this.drag.startX = e.clientX;
    this.drag.startY = e.clientY;
    this.drag.scrollX = this.scroll.target.x;
    this.drag.scrollY = this.scroll.target.y;
  }

  onMouseUp() {
    this.isDragging = false;
    document.documentElement.classList.remove('dragging');
    this.mouse.press.t = 0;
  }

  onMouseMove(e) {
    this.mouse.x.t = e.clientX / this.winW;
    this.mouse.y.t = e.clientY / this.winH;

    if (this.isDragging) {
      const dx = e.clientX - this.drag.startX;
      const dy = e.clientY - this.drag.startY;
      this.scroll.target.x = this.drag.scrollX + dx;
      this.scroll.target.y = this.drag.scrollY + dy;
    }
  }

  onTouchStart(e) {
    e.preventDefault();
    if (e.touches.length > 0) {
      this.isDragging = true;
      document.documentElement.classList.add('dragging');
      this.mouse.press.t = 1;
      this.drag.startX = e.touches[0].clientX;
      this.drag.startY = e.touches[0].clientY;
      this.drag.scrollX = this.scroll.target.x;
      this.drag.scrollY = this.scroll.target.y;
    }
  }

  onTouchMove(e) {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      this.mouse.x.t = touch.clientX / this.winW;
      this.mouse.y.t = touch.clientY / this.winH;

      if (this.isDragging) {
        e.preventDefault();
        const dx = touch.clientX - this.drag.startX;
        const dy = touch.clientY - this.drag.startY;
        this.scroll.target.x = this.drag.scrollX + dx;
        this.scroll.target.y = this.drag.scrollY + dy;
      }
    }
  }

  onTouchEnd(e) {
    this.isDragging = false;
    document.documentElement.classList.remove('dragging');
    this.mouse.press.t = 0;
  }

  render() {
    this.scroll.current.x += (this.scroll.target.x - this.scroll.current.x) * this.scroll.ease;
    this.scroll.current.y += (this.scroll.target.y - this.scroll.current.y) * this.scroll.ease;

    this.scroll.delta.x.t = this.scroll.current.x - this.scroll.last.x;
    this.scroll.delta.y.t = this.scroll.current.y - this.scroll.last.y;
    this.scroll.delta.x.c += (this.scroll.delta.x.t - this.scroll.delta.x.c) * 0.04;
    this.scroll.delta.y.c += (this.scroll.delta.y.t - this.scroll.delta.y.c) * 0.04;
    this.mouse.x.c += (this.mouse.x.t - this.mouse.x.c) * 0.04;
    this.mouse.y.c += (this.mouse.y.t - this.mouse.y.c) * 0.04;
    this.mouse.press.c += (this.mouse.press.t - this.mouse.press.c) * 0.04;

    const dirX = this.scroll.current.x > this.scroll.last.x ? 'right' : 'left';
    const dirY = this.scroll.current.y > this.scroll.last.y ? 'down'  : 'up';

    this.items.forEach(item => {
      const newX = 5 * this.scroll.delta.x.c * item.ease + (this.mouse.x.c - 0.5) * item.rect.width * 0.6;
      const newY = 5 * this.scroll.delta.y.c * item.ease + (this.mouse.y.c - 0.5) * item.rect.height * 0.6;
      const scrollX = this.scroll.current.x;
      const scrollY = this.scroll.current.y;
      const posX = item.x + scrollX + item.extraX + newX;
      const posY = item.y + scrollY + item.extraY + newY;

      const beforeX = posX > this.winW;
      const afterX = posX + item.rect.width < 0;
      if (dirX === 'right' && beforeX) item.extraX -= this.tileSize.w;
      if (dirX === 'left' && afterX) item.extraX += this.tileSize.w;

      const beforeY = posY > this.winH;
      const afterY = posY + item.rect.height < 0;
      if (dirY === 'down' && beforeY) item.extraY -= this.tileSize.h;
      if (dirY === 'up' && afterY) item.extraY += this.tileSize.h;

      const fx = item.x + scrollX + item.extraX + newX;
      const fy = item.y + scrollY + item.extraY + newY;
      item.el.style.transform = `translate(${fx}px, ${fy}px)`;
      item.img.style.transform = `scale(${1.2 + 0.2 * this.mouse.press.c * item.ease}) translate(${-this.mouse.x.c * item.ease * 10}%, ${-this.mouse.y.c * item.ease * 10}%)`;

      // Check if item is near viewport (with extended margin for preloading)
      const preloadMarginX = this.winW * 0.5; // Preload images 50% viewport away
      const preloadMarginY = this.winH * 0.5;
      const isNearViewport =
        fx + item.rect.width > -preloadMarginX &&
        fx < this.winW + preloadMarginX &&
        fy + item.rect.height > -preloadMarginY &&
        fy < this.winH + preloadMarginY;

      // Lazy load images when they're near viewport
      if (isNearViewport && !item.imageRequested && item.img.dataset.src) {
        item.imageRequested = true;

        // Set up transition for all images
        item.img.style.transition = 'opacity 0.8s ease-out, filter 0.8s ease-out';

        // Two-stage loading for performance:
        if (item.img.dataset.thumbnail && !item.img.src) {
          // Stage 1: Load low-quality thumbnail first (fast)
          item.img.style.filter = 'blur(20px)';
          item.img.src = item.img.dataset.thumbnail;
          item.img.classList.add('loaded');

          // Stage 2: Load full quality image in background
          const fullImg = new Image();
          fullImg.onload = () => {
            // Replace with full image but KEEP IT BLURRED
            item.img.src = item.img.dataset.src;
            item.img.style.filter = 'blur(15px)';
            item.fullImageLoaded = true;
          };
          fullImg.src = item.img.dataset.src;
        } else {
          // No thumbnail available - load full image directly
          item.img.style.filter = 'blur(15px)';
          item.img.src = item.img.dataset.src;
          item.img.classList.add('loaded');
          item.fullImageLoaded = true;
        }
      }

      // Check if item is in the CENTER of viewport (focus area)
      const focusMarginX = this.winW * 0.05; // Clear when in center 90% of viewport (very generous)
      const focusMarginY = this.winH * 0.05;
      const isInFocusX = fx + item.rect.width > focusMarginX && fx < this.winW - focusMarginX;
      const isInFocusY = fy + item.rect.height > focusMarginY && fy < this.winH - focusMarginY;
      const isInFocus = isInFocusX && isInFocusY;

      // Only remove blur when full image is loaded AND in focus area
      if (item.fullImageLoaded && !item.isCleared) {
        if (isInFocus) {
          // Image entered focus area - remove blur
          item.img.style.filter = 'blur(0px)';
          item.isCleared = true;
          item.isViewed = true;
        }
      }
    });

    this.scroll.last.x = this.scroll.current.x;
    this.scroll.last.y = this.scroll.current.y;

    requestAnimationFrame(this.render);
  }

  destroy() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('mousemove', this.onMouseMove);
    this.$container.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    this.$container.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('touchend', this.onTouchEnd);
  }
}
